<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link
        view="AdminAlertPlanNumberList"
        label="Alert Plan Numbers"
        :active="true"
      />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div>
              <h1 class="title mb-2">Manage Alert Plan Numbers</h1>
              <p class="subtitle is-6 has-text-grey mt-0">Total: {{ totalCount }} Plan Numbers</p>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-button type="is-success" @click="openCreateModal">
              <b-icon icon="plus"></b-icon>
              <span>Create New</span>
            </b-button>
          </div>
        </div>
      </div>
      <hr />

      <!-- Search -->
      <b-field>
        <div class="field-body">
          <b-field label="Search planNumber">
            <b-input
              v-model="search"
              placeholder="Search by planNumber..."
              icon="magnify"
              @input="debouncedSearch"
            ></b-input>
          </b-field>
        </div>
      </b-field>

      <div class="table-container">
        <b-table
          :data="planNumber"
          :loading="loading"
          hoverable
          striped
          :paginated="true"
          backend-pagination
          :total="totalCount"
          :per-page="perPage"
          :current-page="currentPage"
          @page-change="onPageChange"
          pagination-position="bottom"
          default-sort="planNumber"
          default-sort-direction="asc"
        >
          <b-table-column field="planNumber" label="Plan Number" v-slot="props" sortable>
            <span class="has-text-weight-semibold">{{ props.row.plan_number }}</span>
          </b-table-column>

          <b-table-column label="Actions" width="160" v-slot="props">
            <div class="field is-grouped">
              <div class="control">
                <b-button
                  type="is-primary is-outlined"
                  size="is-small"
                  icon-left="pencil"
                  @click="openEditModal(props.row)"
                >
                  Edit
                </b-button>
              </div>
              <div class="control">
                <b-button
                  type="is-danger"
                  size="is-small"
                  icon-left="delete"
                  @click="confirmDelete(props.row)"
                >
                  Delete
                </b-button>
              </div>
            </div>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered">
              <b-icon icon="emoticon-sad" size="is-large"></b-icon>
              <p>No planNumber found.</p>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Create Modal -->
      <b-modal v-model="isCreateModalActive" :width="500" scroll="keep">
        <form @submit.prevent="createPlanNumbers">
          <div class="modal-card">
            <header class="modal-card-head">
              <p class="modal-card-title">Create New planNumber</p>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="isCreating"></b-loading>

              <b-field
                label="Plan Number"
                required
                :type="{ 'is-danger': !!createErrors.plan_number }"
                :message="createErrors.plan_number"
              >
                <b-input
                  v-model="createForm.plan_number"
                  placeholder="Enter Plan Number"
                  required
                  maxlength="255"
                ></b-input>
              </b-field>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="closeCreateModal">Cancel</b-button>
              <b-button type="is-success" native-type="submit" :loading="isCreating">
                Create
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>

      <!-- Edit Modal -->
      <b-modal v-model="showEditModal" :width="500" scroll="keep">
        <form @submit.prevent="handleUpdatePlanNumber">
          <div class="modal-card" style="width: auto">
            <header class="modal-card-head">
              <p class="modal-card-title">Edit planNumber</p>
              <button type="button" class="delete" @click="showEditModal = false"></button>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="editLoading"></b-loading>

              <b-field label="Plan Number" :type="{ 'is-danger': !!errors.plan_number }" :message="errors.plan_number">
                <b-input
                  v-model="editForm.plan_number"
                  placeholder="Enter Plan Number"
                  required
                  maxlength="255"
                ></b-input>
              </b-field>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="showEditModal = false">Cancel</b-button>
              <b-button type="is-primary" native-type="submit" :loading="editLoading">
                Save Changes
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>
    </div>
  </AdminLayout>
</template>

<script>
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';
export default {
  name: 'AdminAlertPlanNumberList',
  components: {
    AdminLayout,
  },
  data() {
    return {
      loading: false,
      planNumber: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 50,
      search: '',
      searchTimeout: null,
      isCreateModalActive: false,
      createForm: {
        plan_number: '',
      },
      createErrors: {},
      isCreating: false,
      showEditModal: false,
      editLoading: false,
      editingplanNumber: null,
      editForm: {},
      errors: {},
    };
  },
  created() {
    this.loadPlanNumbers();
  },
  methods: {
    async loadPlanNumbers() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.perPage) params.append('limit', this.perPage);
        if (this.currentPage > 1) params.append('page', this.currentPage);
        if (this.search.trim()) params.append('term', this.search.trim());
        const response = await request.get(`/admin/alerts/plan-numbers?${params.toString()}`);
        this.planNumber = response.data.plan_numbers || [];
        this.totalCount = parseInt(response.headers['x-total-count'] || '0');
      } catch (error) {
        console.error('Error loading planNumber:', error);
        this.$buefy.toast.open({
          message: 'Failed to load planNumber',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadPlanNumbers();
    },
    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadPlanNumbers();
      }, 300);
    },
    openCreateModal() {
      this.createForm = {
        plan_number: '',
      };
      this.createErrors = {};
      this.isCreateModalActive = true;
    },
    closeCreateModal() {
      this.isCreateModalActive = false;
      this.createForm = {
        plan_number: '',
      };
      this.createErrors = {};
    },
    async createPlanNumbers() {
      this.isCreating = true;
      this.createErrors = {};
      try {
        const payload = {
          plan_number: this.createForm.plan_number?.trim(),
        };
        await request.post('/admin/alerts/plan-numbers', payload);
        this.$buefy.toast.open({
          message: 'Plan Number created successfully',
          type: 'is-success',
        });
        this.closeCreateModal();
        this.loadPlanNumbers();
      } catch (error) {
        console.error('Error creating planNumber:', error);
        if (error.response?.data?.errors) {
          this.createErrors = error.response.data.errors;
        } else {
          const msg = error.response?.data?.code == 422 ? 'Plan Number already exists' : 'Failed to create plan number';
          this.$buefy.toast.open({
            message: msg,
            type: 'is-danger',
          });
        }
      } finally {
        this.isCreating = false;
      }
    },
    openEditModal(obj) {
      this.editingplanNumber = obj;
      this.editForm = {
        plan_number: obj.plan_number,
      };
      this.errors = {};
      this.showEditModal = true;
    },
    async handleUpdatePlanNumber() {
      this.editLoading = true;
      this.errors = {};
      try {
        const payload = {
          plan_number: this.editForm.plan_number?.trim(),
        };
        await request.put(`/admin/alerts/plan-numbers/${this.editingplanNumber.id}`, payload);
        this.$buefy.toast.open({
          message: 'planNumber updated successfully',
          type: 'is-success',
        });
        this.showEditModal = false;
        this.loadPlanNumbers();
      } catch (error) {
        console.error('Error updating planNumber:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else if (error.response?.data?.message) {
          let msg = error.response?.data?.code == 422 ? 'Plan Number already exists' : 'Failed to update plan number';
          this.$buefy.toast.open({
            message: msg,
            type: 'is-danger',
          });
        } else {
          this.$buefy.toast.open({
            message: 'Failed to update planNumber',
            type: 'is-danger',
          });
        }
      } finally {
        this.editLoading = false;
      }
    },
    confirmDelete(obj) {
      this.$buefy.dialog.confirm({
        title: 'Delete Plan Number',
        message: `Are you sure you want to delete "<strong>${obj.plan_number}</strong>"? This action cannot be undone.`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deletePlanNumber(obj),
      });
    },
    async deletePlanNumber(obj) {
      try {
        await request.delete(`/admin/alerts/plan-numbers/${obj.id}`);
        this.$buefy.toast.open({
          message: 'Plan Number deleted successfully',
          type: 'is-success',
        });
        this.loadPlanNumbers();
      } catch (error) {
        console.error('Error deleting planNumber:', error);
        let errorMessage = 'Failed to delete planNumber';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.status === 400) {
          errorMessage = 'Cannot delete this planNumber - it may still be in use by alerts';
        }
        // Show error message in a dialog for better visibility
        this.$buefy.dialog.alert({
          title: 'Delete Failed',
          message: errorMessage,
          type: 'is-danger',
          hasIcon: true,
          ariaRole: 'alertdialog',
          ariaModal: true,
        });
      }
    },
  },
};
</script>
